import React, { useState, useEffect } from "react";
import {
  BookOpen,
  RefreshCcw,
  FileText,
  Clock,
  ChevronRight,
  Search,
} from "lucide-react";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  CounselorResourceService,
  type CounselorResource,
} from "@/services/counselor-resources";

export const ResourcesPage: React.FC = () => {
  const [, setLocation] = useLocation();
  const [resources, setResources] = useState<CounselorResource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Load resources on component mount
  useEffect(() => {
    loadResources();
  }, [page, searchTerm]);

  const loadResources = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await CounselorResourceService.getPublishedResources({
        page,
        limit: 10,
        search: searchTerm || undefined,
        sortBy: "order",
        sortOrder: "ASC",
      });

      setResources(response.resources);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error("Failed to load counselor resources:", err);
      setError("Failed to load resources. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleResourceClick = (resourceId: string) => {
    setLocation(`/resources/${resourceId}`);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  if (isLoading && resources.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading resources...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <BookOpen className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Resources</h1>
            <p className="text-muted-foreground">
              Access important resources and guidelines ({total} resources
              available)
            </p>
          </div>
        </div>
        <button
          onClick={loadResources}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 text-sm text-muted-foreground border border-border rounded-md hover:bg-accent transition-colors"
        >
          <RefreshCcw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <input
          type="text"
          placeholder="Search resources..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Resources List */}
      {resources.length === 0 && !isLoading ? (
        <div className="bg-background border border-border rounded-md p-12">
          <div className="text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
            <h3 className="text-xl font-medium text-foreground mb-2">
              {searchTerm ? "No resources found" : "No resources available"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try adjusting your search terms or clear the search to see all resources."
                : "There are currently no resources published by administrators."}
            </p>
            {searchTerm && (
              <button
                onClick={() => handleSearch("")}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <span>Clear Search</span>
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {resources.map((resource) => (
            <div
              key={resource.id}
              onClick={() => handleResourceClick(resource.id)}
              className="bg-background border border-border rounded-md p-6 hover:bg-accent/50 transition-colors cursor-pointer group"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                  </div>

                  {resource.description && (
                    <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
                      {resource.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        Updated{" "}
                        {new Date(resource.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>

                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-4" />
              </div>
            </div>
          ))}

          {/* Loading indicator for pagination */}
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">
                Loading...
              </span>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4">
              <div className="text-sm text-muted-foreground">
                Showing page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className={cn(
                    "px-3 py-1 text-sm border border-border rounded-md transition-colors",
                    page === 1
                      ? "text-muted-foreground cursor-not-allowed opacity-50"
                      : "text-foreground hover:bg-accent"
                  )}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={cn(
                        "px-3 py-1 text-sm border rounded-md transition-colors",
                        page === pageNum
                          ? "bg-primary text-primary-foreground border-primary"
                          : "text-foreground border-border hover:bg-accent"
                      )}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className={cn(
                    "px-3 py-1 text-sm border border-border rounded-md transition-colors",
                    page === totalPages
                      ? "text-muted-foreground cursor-not-allowed opacity-50"
                      : "text-foreground hover:bg-accent"
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
